import { View } from 'web-mojo';

/**
 * StopInsertionDrawer — insert a stop into an active route.
 * Different from StopConfigDrawer (which works on templates).
 * Submits via PATCH /api/routes/route/{id} { action: 'insert_stop', ... }
 * Emits 'inserted' on success.
 */
export default class StopInsertionDrawer extends View {
    constructor({ route, stops = [] }) {
        super();
        this.route = route;
        this.stops = stops;
        this.locations = [];
        this.machines = [];
        this.selectedLocationId = null;
    }

    async onInit() {
        const response = await app.api.get('/api/camaccess/location', {
            group: app.groupId(),
            is_active: 'true',
            size: 200,
            sort: 'name',
        });
        if (response.success && response.data.status) {
            this.locations = response.data.data || [];
        }
    }

    async onActionLocationChange(event, el) {
        const locationId = el.value;
        this.selectedLocationId = locationId || null;

        if (locationId) {
            const response = await app.api.get('/api/camaccess/machine', {
                location: locationId,
                is_active: 'true',
                size: 200,
            });
            this.machines = (response.success && response.data.status)
                ? response.data.data || []
                : [];
        } else {
            this.machines = [];
        }

        const machineSection = this.element.querySelector('[data-container="machine-section"]');
        if (machineSection) {
            machineSection.innerHTML = this._machineSectionHtml();
        }
    }

    async onActionInsert() {
        const activityType = this.element.querySelector('[name=activity_type]')?.value;
        if (!activityType) {
            app.toast.warning('Activity type is required');
            return;
        }

        const labelEl = this.element.querySelector('[name=label]');
        const label = labelEl?.value?.trim();
        const locationId = this.element.querySelector('[name=location]')?.value;
        const machineId = this.element.querySelector('[name=machine]')?.value;
        const afterStopId = this.element.querySelector('[name=after_stop_id]')?.value;
        const dwellMins = parseInt(this.element.querySelector('[name=dwell_minutes]')?.value || '0') || null;
        const priority = this.element.querySelector('[name=priority]')?.value || 'normal';
        const plannerNote = this.element.querySelector('[name=planner_note]')?.value?.trim() || null;

        if (!label && !locationId) {
            app.toast.warning('Enter a label or select a location');
            return;
        }

        const payload = {
            action: 'insert_stop',
            activity_type: activityType,
            priority,
            planner_note: plannerNote,
        };

        if (label) payload.label = label;
        if (locationId) payload.location_id = parseInt(locationId);
        if (machineId) payload.machine_id = parseInt(machineId);
        if (afterStopId) payload.after_stop_id = parseInt(afterStopId);
        if (dwellMins) payload.dwell_minutes = dwellMins;

        const response = await app.api.patch(`/api/routes/route/${this.route.id}`, payload);
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to insert stop');
            return;
        }

        this.emit('inserted', response.data.data);
    }

    _machineSectionHtml() {
        if (!this.selectedLocationId) {
            return '<p class="text-muted small mb-0">Select a location to load machines.</p>';
        }
        if (this.machines.length === 0) {
            return '<p class="text-muted small mb-0">No machines at this location.</p>';
        }
        const options = this.machines.map(m =>
            `<option value="${m.id}">${m.name}${m.machine_type ? ` · ${m.machine_type}` : ''}</option>`
        ).join('');
        return `
            <select class="form-select" name="machine">
                <option value="">No specific machine</option>
                ${options}
            </select>
        `;
    }

    set template(v) { this._t = v; }

    get template() {
        const pendingStops = this.stops.filter(s => s.status === 'pending');

        const locationOptions = this.locations.map(loc =>
            `<option value="${loc.id}">${loc.name}</option>`
        ).join('');

        const afterStopOptions = this.stops
            .filter(s => !['removed', 'cancelled'].includes(s.status))
            .map((s, i) => {
                const label = s.label || s.location?.name || `Stop ${i + 1}`;
                return `<option value="${s.id}">${label}</option>`;
            }).join('');

        const activityOptions = ['stocking', 'maintenance', 'collection', 'mixed'].map(a =>
            `<option value="${a}">${a.charAt(0).toUpperCase() + a.slice(1)}</option>`
        ).join('');

        const priorityOptions = ['normal', 'high', 'urgent'].map(p =>
            `<option value="${p}">${p.charAt(0).toUpperCase() + p.slice(1)}</option>`
        ).join('');

        return `
            <div class="p-3">
                <div class="alert alert-warning small py-2 mb-3">
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    The operator will be notified of this route change.
                </div>

                <div class="mb-3">
                    <label class="form-label">Label</label>
                    <input type="text" class="form-control" name="label" placeholder="e.g. Priority job — customer on site">
                    <div class="form-text">Or select a location below to auto-fill.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Location</label>
                    <select class="form-select" name="location" data-action="location-change">
                        <option value="">No location (ad-hoc)</option>
                        ${locationOptions}
                    </select>
                </div>

                <div class="mb-3">
                    <label class="form-label">Machine</label>
                    <div data-container="machine-section">
                        ${this._machineSectionHtml()}
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Activity Type *</label>
                    <select class="form-select" name="activity_type">
                        ${activityOptions}
                    </select>
                </div>

                <div class="row g-3 mb-3">
                    <div class="col-6">
                        <label class="form-label">Dwell Time (min)</label>
                        <input type="number" class="form-control" name="dwell_minutes" min="0" placeholder="e.g. 20">
                    </div>
                    <div class="col-6">
                        <label class="form-label">Priority</label>
                        <select class="form-select" name="priority">
                            ${priorityOptions}
                        </select>
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Insert After Stop *</label>
                    <select class="form-select" name="after_stop_id">
                        <option value="">Insert at beginning</option>
                        ${afterStopOptions}
                    </select>
                </div>

                <div class="mb-4">
                    <label class="form-label">Planner Note to Operator</label>
                    <textarea class="form-control" name="planner_note" rows="2"
                        placeholder="e.g. Priority job — customer on site"></textarea>
                </div>

                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-primary" data-action="insert">
                        <i class="bi bi-plus-circle me-1"></i>Insert Stop
                    </button>
                </div>
            </div>
        `;
    }
}
